import DOMPurify from "dompurify";

/**
 * SS-4071 — sanitizer for the HTML an AI chat answer is injected with.
 *
 * Office Optimizer answers (and the user's own input) go straight into
 * dangerouslySetInnerHTML. Two problems came out of that: markup the app can
 * never render — a Chart.js <canvas>/<script> block showed up as a blank
 * 400x200 box, because scripts inserted via innerHTML do not execute — and
 * markup that DOES run once injected, such as an inline onerror handler, in
 * HTML that is partly derived from patient data.
 *
 * So allow only what the chat legitimately uses: text and formatting, lists,
 * links, and the tables the app turns into bar charts (see MessageList's
 * parseTable). data-* attributes stay allowed by default, which the hidden
 * <span class="support-help" data-question="…"> the API embeds relies on.
 *
 * SS-4106 — links get the same treatment. After creating a campaign the
 * assistant wrote "Click here to review and approve the campaign" behind an
 * anchor to app.dentalflow.ai (app.dentalemr.com on another practice) at
 * /campaigns/<id>: neither host is Viva's and that route does not exist here,
 * so the office left the product and the draft was never approved. The host
 * is made up per answer, so an anchor keeps its href only when the href is
 * one this app can actually serve.
 *
 * The tag allowlist mirrors the API's po/utils/htmlSanitize.js, which strips
 * the same markup before storing an answer — keep the two in sync, link
 * policy included. The attribute allowlist is the same set, but DOMPurify
 * applies it globally rather than per tag (the API scopes href to <a> and
 * colspan to cells); the extra leeway is inert markup, and DOMPurify still
 * filters unsafe URLs.
 */
export const ALLOWED_TAGS = [
  "p", "br", "hr", "div", "span",
  "strong", "b", "em", "i", "u", "s", "small", "sub", "sup",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "dl", "dt", "dd",
  "blockquote", "code", "pre",
  "a",
  "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
];

export const ALLOWED_ATTR = [
  "class",
  "href", "target", "rel", "title",
  "colspan", "rowspan", "scope",
];

/**
 * The origins an answer may link to: the host this app is served from, plus
 * every app URL the API's po/core/config.js can put in a tool result — the
 * same three values, kept in sync with it.
 *
 * All three rather than only this deployment's: the API builds its links from
 * ITS stage's APP_URL, a white-label build is served from its own host, and
 * the client has no reliable way to know which API composed a stored answer
 * (the build-time stage variable is not set everywhere, and guessing it wrong
 * would quietly unwrap every legitimate link). All three are Viva app hosts
 * behind our own login; the API's sanitizer still holds stored answers to the
 * exact stage. Anything else — an invented dental-SaaS domain, a lookalike of
 * ours, a protocol-relative //host — is not somewhere the office is logged in,
 * so its anchor loses the href.
 */
const APP_ORIGINS = [
  "http://localhost:3001",
  "https://qa-app.getviva.ai",
  "https://app.getviva.ai",
];

const isAllowedChatUrl = (href) => {
  if (typeof href !== "string" || !href.trim()) return false;
  const origin = window.location.origin;
  let url;
  try {
    // Resolving against our own origin is what makes a relative href allowed
    // and a protocol-relative one ("//other.host/x") correctly foreign.
    url = new URL(href.trim(), origin);
  } catch {
    return false;
  }
  // mailto:/tel: are what an answer legitimately contacts somebody with; they
  // open a mail client or dialler, not a page that can pose as the product.
  if (url.protocol === "mailto:" || url.protocol === "tel:") return true;
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  return url.origin === origin || APP_ORIGINS.includes(url.origin);
};

/**
 * A `RETURN_DOM_FRAGMENT` pass rather than a DOMPurify hook, for the reason
 * Support/supportBodySanitize.js gives: hooks are global to the module, and
 * Inbox/Messages.js shares this DOMPurify — a hook registered here would
 * rewrite team-chat messages too.
 */
export const sanitizeChatHtml = (html) => {
  if (typeof html !== "string" || !html) return "";
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_DOM_FRAGMENT: true,
  });
  fragment.querySelectorAll("a[href]").forEach((anchor) => {
    // Unwrap rather than drop: the sentence still reads, it just stops being a
    // link out of the product. An <a> with no href renders as plain text.
    if (!isAllowedChatUrl(anchor.getAttribute("href"))) anchor.removeAttribute("href");
  });
  const holder = fragment.ownerDocument.createElement("div");
  holder.appendChild(fragment);
  return holder.innerHTML;
};
